import { MouseEvent, ReactNode } from "react";
import classnames from "classnames";
import { TextLinkRole, TextLinkSize, TextLinkTheme } from "./text-link.types";
import styles from "./text-link.styles.module.scss";

type TextLinkProps = {
  href: string;
  isDisabled?: boolean;
  onClick?: (event: MouseEvent) => void;
  role?: TextLinkRole;
  size?: TextLinkSize;
  text: string | null;
  theme?: TextLinkTheme;
  variant?: "subtle" | "prominent";
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  isUnderlined?: boolean;
  rootClassName?: string;
  ariaLabel?: string,
  isMasked?: boolean,
};

export const TextLinkComponent = ({
  href = "",
  isDisabled = false,
  onClick = () => {},
  role = "link",
  size = "regular",
  text = "",
  theme = "light",
  variant = "subtle",
  startIcon = null,
  endIcon = null,
  isUnderlined = false,
  rootClassName = "",
  ariaLabel = "",
  isMasked = false,
}: TextLinkProps) => {
  const handleClick = (event: MouseEvent) => {
    event.preventDefault();
    if (isDisabled) {
      return;
    }
    onClick(event);
  };

  const hasIcon = Boolean(startIcon || endIcon);
  const underlineClass = {
    [styles.iconUnderline]: hasIcon && isUnderlined,
    [styles.iconHoverUnderline]: hasIcon && !isUnderlined,
    [styles.textDecorationUnderline]: !hasIcon && isUnderlined,
    [styles.textHoverUnderline]: !hasIcon && !isUnderlined,
    [styles.textLinkContainerIcon]: hasIcon,
  };

  if(!text) {
    return <></>;
  }

  return (
    <a
      aria-disabled={isDisabled}
      aria-label={ariaLabel}
      className={classnames(
        styles.textLinkContainer,
        size === "regular" && styles.regularSize,
        size === "small" && styles.smallSize,
        theme === "dark" && styles.darkTheme,
        theme === "light" && styles.lightTheme,
        isDisabled && styles.disabledTextLink,
        variant === "subtle" && styles.subtleVariant,
        variant === "prominent" && styles.prominentVariant,
        underlineClass,
        rootClassName
      )}
      href={href}
      onClick={handleClick}
      role={role}
      tabIndex={0}
      {...(isMasked ? { "data-amp-mask-attributes": "href" } : {})}
    >
      <span className={styles.textLinkContent}>
        {startIcon && (
          <span
            className={classnames(
              size === "regular" && styles.iconRegular,
              size === "small" && styles.iconSmall
            )}
          >
            {startIcon}
          </span>
        )}
        <span {...(isMasked ? { "data-amp-mask": "" } : {})}>{text}</span>
        {endIcon && (
          <span
            className={classnames(
              size === "regular" && styles.iconRegular,
              size === "small" && styles.iconSmall
            )}
          >
            {endIcon}
          </span>
        )}
      </span>
    </a>
  );
};
